import type { Dispatch, SetStateAction } from "react";
import {
  salesIndigoInsetCardClass,
  salesIndigoInsetInputClass,
  salesIndigoOutlineBtnClass,
  salesIndigoPanelClass,
} from "@/components/common/surfaceClasses";
import { SalesConsoleCollapsibleSection } from "./SalesConsoleCollapsibleSection";

type StickyNote = { title: string; body: string };

type Props = {
  canEditStickyNote: boolean;
  stickyNotes: StickyNote[];
  stickyNotesDraft: StickyNote[];
  stickySaving: boolean;
  stickyDirty: boolean;
  stickyMsg: { ok: boolean; text: string } | null;
  maxStickyNotes: number;
  normalizeStickyNotes: (notes: StickyNote[]) => StickyNote[];
  setStickyNotesDraft: Dispatch<SetStateAction<StickyNote[]>>;
  setStickyMsg: Dispatch<SetStateAction<{ ok: boolean; text: string } | null>>;
  onSaveStickyNote: () => void;
};

export function SalesConsoleStickyNotesSection(props: Props) {
  const draft = props.stickyNotesDraft.length ? props.stickyNotesDraft : [{ title: "", body: "" }];
  const visibleStickyNotes = props.normalizeStickyNotes(props.stickyNotes);
  return (
    <SalesConsoleCollapsibleSection title="Sales Console Sticky Notes" subtitle="Org-wide reminders visible to the sales team" defaultOpen fireflyTargetId="sc-sticky-notes">
      <div className={salesIndigoPanelClass}>
        <div className="flex items-center justify-between gap-3">
          {props.canEditStickyNote ? (
            <span className="text-[11px] text-indigo-700 dark:text-indigo-300">
              Trainer+ can edit ({props.normalizeStickyNotes(props.stickyNotesDraft).length}/{props.maxStickyNotes})
            </span>
          ) : (
            <span className="text-[11px] text-indigo-700 dark:text-indigo-300">Read-only</span>
          )}
        </div>
        {props.canEditStickyNote ? (
          <div className="mt-3 space-y-2">
            {draft.length > 1 ? (
              <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                {draft.map((note, idx) => (
                  <div key={idx} className={`${salesIndigoInsetCardClass} p-2 space-y-1.5`}>
                    <input
                      type="text"
                      value={note.title}
                      maxLength={60}
                      onChange={(e) =>
                        props.setStickyNotesDraft((prev) => {
                          const base = prev.length ? [...prev] : [{ title: "", body: "" }];
                          base[idx] = { ...base[idx]!, title: e.target.value };
                          return base.slice(0, props.maxStickyNotes);
                        })
                      }
                      placeholder="Title"
                      className={salesIndigoInsetInputClass}
                    />
                    <textarea
                      value={note.body}
                      maxLength={220}
                      onChange={(e) =>
                        props.setStickyNotesDraft((prev) => {
                          const base = prev.length ? [...prev] : [{ title: "", body: "" }];
                          base[idx] = { ...base[idx]!, body: e.target.value };
                          return base.slice(0, props.maxStickyNotes);
                        })
                      }
                      rows={3}
                      placeholder="Short note"
                      className={salesIndigoInsetInputClass}
                    />
                    <button
                      type="button"
                      onClick={() => props.setStickyNotesDraft((prev) => {
                        const next = [...prev];
                        next.splice(idx, 1);
                        return next;
                      })}
                      className="text-[11px] text-red-700 underline dark:text-red-400"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`${salesIndigoInsetCardClass} p-2 space-y-1.5`}>
                <input
                  type="text"
                  value={draft[0]!.title}
                  maxLength={60}
                  onChange={(e) =>
                    props.setStickyNotesDraft((prev) => {
                      const next = prev.length ? [...prev] : [{ title: "", body: "" }];
                      next[0] = { ...next[0]!, title: e.target.value };
                      return next;
                    })
                  }
                  placeholder="Title"
                  className={salesIndigoInsetInputClass}
                />
                <textarea
                  value={draft[0]!.body}
                  maxLength={220}
                  onChange={(e) =>
                    props.setStickyNotesDraft((prev) => {
                      const next = prev.length ? [...prev] : [{ title: "", body: "" }];
                      next[0] = { ...next[0]!, body: e.target.value };
                      return next;
                    })
                  }
                  rows={3}
                  placeholder="Short note"
                  className={salesIndigoInsetInputClass}
                />
              </div>
            )}
            <div>
              <button
                type="button"
                disabled={props.stickyNotesDraft.length >= props.maxStickyNotes}
                onClick={() => props.setStickyNotesDraft((prev) => (prev.length >= props.maxStickyNotes ? prev : [...prev, { title: "", body: "" }]))}
                className={salesIndigoOutlineBtnClass}
              >
                Add note
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={props.onSaveStickyNote}
                disabled={props.stickySaving || !props.stickyDirty}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
              >
                {props.stickySaving ? "Saving..." : "Save sticky note"}
              </button>
              {props.stickyDirty ? (
                <button
                  type="button"
                  onClick={() => {
                    props.setStickyNotesDraft(props.stickyNotes);
                    props.setStickyMsg(null);
                  }}
                  className={`${salesIndigoOutlineBtnClass} px-3 py-1.5`}
                >
                  Discard
                </button>
              ) : null}
            </div>
            {props.stickyMsg ? (
              <p className={`text-xs ${props.stickyMsg.ok ? "text-emerald-700 dark:text-emerald-300" : "text-red-700 dark:text-red-400"}`}>
                {props.stickyMsg.text}
              </p>
            ) : null}
          </div>
        ) : visibleStickyNotes.length === 0 ? (
          <div className={`mt-3 ${salesIndigoInsetCardClass} p-3 text-sm text-indigo-950 dark:text-indigo-100 whitespace-pre-wrap`}>
            No organization sticky notes set
          </div>
        ) : (
          <div className={"mt-3 grid gap-2 " + (visibleStickyNotes.length > 1 ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1")}>
            {visibleStickyNotes.map((note, idx) => (
              <div key={`${note.title}-${idx}`} className={`${salesIndigoInsetCardClass} p-3`}>
                {note.title ? <p className="text-xs font-semibold text-indigo-900 dark:text-indigo-200">{note.title}</p> : null}
                {note.body ? <p className={`mt-1 text-xs whitespace-pre-wrap text-indigo-950 dark:text-indigo-100`}>{note.body}</p> : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </SalesConsoleCollapsibleSection>
  );
}
